import AccountCard from "@/components/features/account/account-card/accountCard";
import useAuth from "@/hooks/useAuth";
import {
  Box,
  Button,
  Drawer,
  Flex,
  Group,
  rem,
  Select,
  Space,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { IconChevronDown } from "@tabler/icons-react";
import { get } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

interface Props {
  opened: boolean;
  onClose: () => void;
  onSubmit: () => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
  infoLock?: {
    id: string;
    approvalUserNames: string;
    numberOfApprovals: number;
  };
}
const LockAccountDrawer = ({
  opened,
  onClose,
  onSubmit,
  isLoading,
  title,
  description,
  infoLock,
}: Props) => {
  const { user } = useAuth();
  const role = useMemo(() => get(user, "userRole", ""), [user]);
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    reason: Yup.string()
      .required(`${t("REQUIRE")}`)
      .max(255, `${t("MAXLENGTH_REJECT")}`),
  });

  const form = useForm({
    initialValues: {
      reason: "",
    },
    validate: yupResolver(schema),
  });

  const handleSubmit = () => {
    localStorage.setItem("closeReason", form.values.reason);
    onSubmit();
  };
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={`${t("LOCK_ACCOUNT_REQUEST")}`}
      withCloseButton
      position="right"
      size={548}
      styles={{
        title: { fontWeight: 700 },
        header: { borderBottom: "1px solid #e0e0e0" },
      }}
    >
      {/* Drawer content */}
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Space h={16} />
        <AccountCard title={title} description={description} />
        {role === "MAKER" && (
          <>
            <Text my={20} fz={14} fw={600} c="#212936">
              {t("APPROVAL_INFORMATION")}
            </Text>
            <Stack spacing={12}>
              <Flex justify="space-between">
                <Text c="dimmed" fz={14}>{t("APPROVAL_ID")}</Text>
                <Text fw={500} fz={14}>{infoLock?.id}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text c="dimmed" fz={14}>{t("APPROVAL_LEVEL")}</Text>
                <Text fw={500} fz={14}>{infoLock?.numberOfApprovals}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text c="dimmed" fz={14}>{t("REVIEWER")}</Text>
                <Text fw={500} fz={14}>{infoLock?.approvalUserNames}</Text>
              </Flex>
            </Stack>
          </>
        )}
        <Stack mt={16}>
          <Select
            data={["SMS OTP"]}
            defaultValue="SMS OTP"
            label={`${t("AUTHENTICATION_MODE")}`}
            rightSection={<IconChevronDown size={16} />}
            radius={8}
          />
          <Textarea
            label={`${t("LOCK_ACCOUNT_REASON")}`}
            minRows={4}
            withAsterisk
            radius={8}
            placeholder={`${t("ENTER_LOCK_ACCOUNT_REASON")}`}
            {...form.getInputProps("reason")}
          />
        </Stack>
        <Group position="center" mt={rem(20)}>
          <Box
            style={{
              position: "fixed",
              bottom: 0,
              right: 0,
              width: "100%",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              boxShadow: "0px 5px 15px 0px #00000026",
              height: "76px",
              alignItems: "center",
              backgroundColor: "white",
              zIndex: 1,
            }}
          >
            <Flex gap={10}>
              <Button
                sx={() => ({
                  width: "100px",
                  backgroundColor: "#F4F4F6",
                  color: "#525866",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 400,
                  "&:hover": {
                    backgroundColor: "#F4F4F6",
                  },
                })}
                onClick={onClose}
              >
                {t("CLOSE")}
              </Button>
              <Button
                loading={isLoading}
                type="submit"
                sx={() => ({
                  width: "160px",
                  backgroundColor: "#2A81D0",
                  color: "white",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 400,
                })}
              >
                {t("CONFIRM")}
              </Button>
            </Flex>
          </Box>
        </Group>
      </form>
    </Drawer>
  );
};

export default LockAccountDrawer;
